<template>
  <div>
    <!-- Framework controls table -->
    <div class="card" :class="{'table-with-filters': filterOpened}">
      <div class="col-sm-10 table-container">
        <div class="row tree-filter-row">
          <div class="col-sm-2 tree-filter-col">
            <div class="card filter-tree-view-container">
              <div class="card-body p-0 tree-filter-col">
                <FrameworkSideFilter
                    v-if="SideFilterRenderComponent"
                    :selectedControlObjective="selectedControlObjective"
                    :selectedControlObjectiveDeleted="selectedControlObjectiveDeleted"
                    :updateList="updateChildList"
                    :framework="framework"
                    @updateSelected="updateSelectedFilter"
                ></FrameworkSideFilter>
              </div>
            </div>
          </div>

          <div class="col-sm-10 tree-result-col">
            <FrameworkTopBar
              :selectedControlObjective="selectedControlObjective"
              :framework="framework"
              @forceSideFilterRenderComponent="forceSideFilterRenderComponent"
            ></FrameworkTopBar>

            <div class="tree-result-main">
              <div class="row">
                <div class="col-md-4">
                </div>
                <div class="col-sm-8 text-right">
                  <br>
                  <button class="btn btn-success"
                          @click="showCreateFrameworkControlModal()"
                          qid="framework-control-create-button"
                          v-if="isGranted(permissions.FRAMEWORK_CONTROL_CREATE) && (!getClient.parent || (getClient.parent && object.partition))"
                  >
                    <font-awesome-icon icon="plus"/> {{ $t('maintenance.framework_control.add_new') }}
                  </button>
                  <b-form-input
                      type="text"
                      v-model="filters.keywordValue"
                      class="float-right mw-300 mr-3 search-bar"
                      v-on:input="debounceInput"
                      :placeholder="$t('maintenance.framework_control.search_placeholder')"
                      qid="do-search-input"
                  ></b-form-input>
                </div>
              </div>

              <table class="table table-responsive-md table-list-secondary">
                <thead>
                  <tr>
                    <th>
                      {{ $t('maintenance.framework_control.table.name') }}
                    </th>
                    <th class="">
                      {{ $t('maintenance.framework_control.table.applicable') }}
                    </th>
                    <th class="">
                      {{ $t('maintenance.framework_control.table.implementation_status') }}
                    </th>
                    <th class="">
                      {{ $t('table.last_updated') }}
                    </th>
                    <th width="180" class="text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(object, index) in tableItems" :key="'table-row-' + index" :id="'table-row-' + index" :class="{ 'alert-disabled' : !object.statement_of_applicability}">
                    <b-popover
                        :target="'table-row-' + index"
                        boundary="viewport"
                        triggers="hover"
                        @show="showFCPopover(object)"
                        @hidden="hideFCPopover()"
                        placement="topleft"
                        :delay="{ show: 500, hide: 0 }"
                    >
                      <div v-if="object.description" class="mt-2">
                        <span class="text-bold">
                          {{ $t('maintenance.framework_control.create.description_label') }}:
                        </span>
                        <br>
                        {{ object.description | truncate(200)}}
                        <br>
                      </div>
                      <div v-if="policyTableItems && policyTableItems.length">
                        <br>
                        <span class="text-bold">
                          {{ $t('maintenance.policies.title') }}:
                        </span>
                        <span v-for="policy in policyTableItems">
                        <br>
                          <span>
                            <font-awesome-icon icon="file-invoice"/><span> {{ policy.name }}</span>
                          </span>
                        </span>
                        <br>
                      </div>
                      <div v-if="genericControlItems && genericControlItems.length">
                        <br>
                        <span class="text-bold">
                          {{ $t('generic_controls.title') }}:
                        </span>
                        <span v-for="genericControl in genericControlItems">
                        <br>
                          <span>
                            <font-awesome-icon icon="shield-alt"/><span> {{ genericControl.name }}</span>
                          </span>
                        </span>
                        <br>
                      </div>
                      <div class="mr-1" v-if="popoverSpinner">
                        <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                      </div>
                    </b-popover>

                    <td>
                      {{ object.full_name }}
                    </td>
                    <td>
                      <span v-if="object.statement_of_applicability">
                        <font-awesome-icon icon="check-circle" class="icon-success"/>
                        {{ $t('system.yes') }}
                      </span>
                      <span v-if="!object.statement_of_applicability">
                        <font-awesome-icon icon="times-circle" class="icon-danger"/>
                        {{ $t('system.no') }}
                      </span>
                    </td>
                    <td>
                      <span v-if="object.implementation_status" class="badge">
                        <font-awesome-icon icon="exclamation-triangle" class="risk-1 mr-2" v-if="object.implementation_status == 'Fully implemented'"/>
                        <font-awesome-icon icon="exclamation-triangle" class="risk-9 mr-2" v-if="object.implementation_status == 'Designed but not implemented' || object.implementation_status == 'Partially implemented'"/>
                        <font-awesome-icon icon="exclamation-triangle" class="risk-16 mr-2" v-if="object.implementation_status == 'Not designed'"/>
                        {{ $t('maintenance.policies.status.' + $options.filters.slugify(object.implementation_status)) }}
                      </span>
                    </td>
                    <td>
                      <div v-if="object.updated_by" class="tree--user-container">
                        <Avatar :object="object.updated_by" class="mr-2"></Avatar>
                        {{ object.updated_at | formatDate }}
                      </div>
                    </td>
                    <td align="right">
                      <span v-if="object.name"
                              @click="openViewFCModal(object)"
                              class="btn btn-view"
                              qid="framework-control-name"
                      >
                        <font-awesome-icon icon="eye"/>
                      </span>
                      <span v-if="isGranted([permissions.FRAMEWORK_CONTROL_EDIT, permissions.FRAMEWORK_CONTROL_EDIT_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                            class="btn btn-edit"
                            qid="edit-framework-control-modal"
                            @click="showEditFrameworkControlModal(object)"
                      >
                        <font-awesome-icon icon="pen"/>
                      </span>
                      <span v-if="isGranted([permissions.FRAMEWORK_CONTROL_DELETE, permissions.FRAMEWORK_CONTROL_DELETE_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                            class="btn btn-delete"
                            qid="delete-framework-control-modal"
                            @click="showConfirmDeleteModal(object)"
                      >
                        <font-awesome-icon icon="trash-alt"/>
                      </span>
                    </td>
                  </tr>

                  <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
                    <td colspan="10">
                      <h5 class="mt-3">
                        {{ $t('system.no_results') }}
                      </h5>
                    </td>
                  </tr>

                  <tr class="text-center" v-if="loading">
                    <td colspan="10">
                      <div class="mt-2 mb-4">
                        <div class="swing-container">
                          <div class="swing">
                            <div class="swing-l"></div>
                            <div></div>
                            <div></div>
                            <div></div>
                            <div class="swing-r"></div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div class="mt-3 pagination row">
                <div class="col-md-6">
                  <div class="row">
                    <div class="float-left">
                      <span class="text-muted pagination-counter">
                        {{ $t('pagination.entries_per_page') }}:
                      </span>
                    </div>
                    <div class="float-left">
                      <multiselect
                          v-model="perPage"
                          :options="perPageOptions"
                          track-by="value"
                          :select-label="$t('system.dropdown_select_label')"
                          :selected-label="$t('system.dropdown_selected_label')"
                          :deselect-label="$t('system.dropdown_deselect_label')"
                          label="value"
                          qid="create-org-form-framework-control"
                          @select="perPageChanged"
                          :placeholder="selectedPerPage"
                          @remove="perPageRemoved"
                      ></multiselect>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <b-pagination
                      class="float-right"
                      v-model="currentPage"
                      :total-rows="rows"
                      :per-page="selectedPerPage"
                      aria-controls="my-table"
                      @change="pageChanged"
                      limit="1"
                  >
                    <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
                    <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
                    <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
                    <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
                    <div slot="ellipsis-text">
                      <b-spinner small type="grow"></b-spinner>
                      <b-spinner small type="grow"></b-spinner>
                      <b-spinner small type="grow"></b-spinner>
                    </div>
                    <span class="text-muted" slot="page" slot-scope="{ page, active }">
                      <span v-if="active">{{getPerPage}}</span>
                    </span>
                  </b-pagination>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete Framework Control modal template -->
    <b-modal ref="delete-framework-control" modal-class="task-modal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideConfirmDeleteModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="delete-fc-title">
            {{ $t('maintenance.framework_control.delete.title') }}
          </h5>
        </div>
      </template>

      <div class="mb-2 mt-3" style="font-size:18px;">
        <div>
          {{ $t('maintenance.framework_control.delete.description') }}
          <span class="text-bold">
            {{ itemToDelete.name }}
          </span>
        </div>
        <div class="mt-2">
          {{ $t('maintenance.framework_control.delete.are_you_sure') }}
        </div>
      </div>

      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class=""
                            variant="secondary"
                            @click="hideConfirmDeleteModal"
                            qid="delete-fc-cancel-button"
                  >
                    {{ $t('maintenance.frameworks.delete.cancel') }}
                  </b-button>
                  <b-button @click="confirmDeleteFrameworkControl(itemToDelete)"
                            class="ml-3"
                            variant="danger"
                            qid="delete-fc-submit-button"
                            v-if="isGranted([permissions.FRAMEWORK_DELETE, permissions.FRAMEWORK_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
                            :disabled="buttonDisabled"
                  >
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                    </span>
                    {{ $t('maintenance.framework_control.delete.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-modal>


    <!-- Add new framework control -->
    <b-modal ref="add-framework-control" @hide="hideCreateFrameworkControlModal" modal-class="task-modal" hide-footer>
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideCreateFrameworkControlModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="add-fc-title">
            {{ $t('maintenance.framework_control.create.title') }}
          </h5>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="add-fc-error"
      >
        {{ error }}
      </b-alert>

      <b-form @submit="fcFormSubmit" qid="create-fc-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-framework-control-reference-label">
              {{ $t('maintenance.frameworks.create.reference_label') }}<span class="mandatory"> *</span>
            </label>
            <b-form-input
                qid="create-framework-control-form-reference"
                type="text"
                v-model="fcForm.reference"
                :maxLength="255"
                required
                :placeholder="$t('maintenance.frameworks.create.reference_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="create-framework-control-name-label">
              {{ $t('maintenance.frameworks.create.name_label') }}<span class="mandatory"> *</span>
            </label>
            <b-form-input
                qid="create-framework-control-form-name"
                type="text"
                v-model="fcForm.name"
                :maxLength="255"
                required
                :placeholder="$t('maintenance.frameworks.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="create-framework-control-description-label">
              {{ $t('maintenance.frameworks.create.description_label') }}<span class="mandatory"> *</span>
            </label>
            <b-form-textarea
                qid="create-framework-control-form-description"
                v-model="fcForm.description"
                rows="6"
                max-rows="6"
                required
                :placeholder="$t('maintenance.frameworks.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>

          <b-form-group>
            <label qid="create-fc-control-objective-label">
              {{ $t('maintenance.control_objective.title') }}<span class="mandatory"> *</span>
            </label>
            <div class="multiselect-row">
              <multiselect
                  v-model="selectedControlObjective"
                  :options="controlObjectives"
                  :placeholder="$t('maintenance.framework_control.create.control_objective_placeholder')"
                  label="name"
                  :select-label="$t('system.dropdown_select_label')"
                  :selected-label="$t('system.dropdown_selected_label')"
                  :deselect-label="$t('system.dropdown_deselect_label')"
                  required
                  :allow-empty="false"
                  @search-change="loadCO"
                  track-by="slug"
                  qid="create-framework-control-form-control-objective"
              >
                <template slot="singleLabel" slot-scope="props">
                  <font-awesome-icon icon="book"/>
                  <span class="ml-2">
                    {{ props.option.name }}
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <font-awesome-icon icon="book"/>
                  <span class="ml-2">
                    {{ props.option.name }}
                  </span>
                </template>
              </multiselect>
              <b-button
                  class="btn btn-secondary float-right"
                  @click="showCreateControlObjectiveModal()"
                  qid="control-objective-create-button-fc-modal"
                  v-if="isGranted(permissions.CONTROL_OBJECTIVE_CREATE) && (!getClient.parent || (getClient.parent && object.partition))"
                  v-b-tooltip.hover
                  :title="$t('maintenance.control_objective.add_new')"
              >
                <font-awesome-icon icon="plus-circle"/>
              </b-button>
            </div>
          </b-form-group>

          <b-form-group>
            <label qid="edit-fc-soa-label">
              {{ $t('maintenance.framework_control.create.statement_of_applicability_label') }}
            </label>
            <div class="row">
              <div class="col-sm-6">
                <p class="text-normal mt-2">{{ $t('maintenance.framework_control.create.applicable_label') }}</p>
              </div>
              <div class="col-sm-6 text-right">
                <b-form-radio-group
                  class="mt-2"
                  v-model="fcForm.statement_of_applicability"
                  :options="radioOptions"
                  qid="edit-fc-form-soa"
                ></b-form-radio-group>
              </div>
            </div>
          </b-form-group>
          <b-form-group>
            <label qid="create-framework-control-description-label">
              {{ $t('maintenance.framework_control.create.soa_description_label') }}
            </label>
            <b-form-textarea
                qid="create-framework-control-form-description"
                v-model="fcForm.statement_of_applicability_description"
                rows="6"
                max-rows="6"
                :placeholder="$t('maintenance.framework_control.create.soa_description_placeholder')"
            ></b-form-textarea>
          </b-form-group>
          <b-form-group>
            <label qid="edit-f-implementation-status-label">
              {{ $t('maintenance.policies.create.implementation_status_label') }}
            </label>
            <multiselect
                v-model="fcForm.implementation_status"
                :options="statusItems"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('maintenance.policies.create.implementation_status_placeholder')"
                label="name"
                qid="edit-f-form-implementation-status"
            >
              <template slot="singleLabel" slot-scope="props">
                <font-awesome-icon icon="exclamation-triangle" class="risk-16 mr-2" v-if="props.option.value == 'Not designed'"/>
                <font-awesome-icon icon="exclamation-triangle" class="risk-9 mr-2" v-if="props.option.value == 'Designed but not implemented' || props.option.value == 'Partially implemented'"/>
                <font-awesome-icon icon="exclamation-triangle" class="risk-1 mr-2" v-if="props.option.value == 'Fully implemented'"/>
                <span class="ml-2">
                  {{ props.option.name }}
                </span>
              </template>
              <template slot="option" slot-scope="props">
                <font-awesome-icon icon="exclamation-triangle" class="risk-16 mr-2" v-if="props.option.value == 'Not designed'"/>
                <font-awesome-icon icon="exclamation-triangle" class="risk-9 mr-2" v-if="props.option.value == 'Designed but not implemented' || props.option.value == 'Partially implemented'"/>
                <font-awesome-icon icon="exclamation-triangle" class="risk-1 mr-2" v-if="props.option.value == 'Fully implemented'"/>
                <span class="ml-2">
                  {{ props.option.name }}
                </span>
              </template>
            </multiselect>
          </b-form-group>
        </div>
        <div class="proxy-footer">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class=""
                            variant="secondary"
                            @click="hideCreateFrameworkControlModal"
                            qid="create-framework-control-cancel-button"
                  >
                    {{ $t('maintenance.frameworks.create.cancel') }}
                  </b-button>
                  <b-button type="submit"
                            class="ml-3"
                            variant="success"
                            :disabled="buttonDisabled"
                            qid="create-framework-control-submit-button"
                  >
                        <span v-if="buttonDisabled" class="mr-1">
                          <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                        </span>
                    {{ $t('maintenance.framework_control.create.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-form>
    </b-modal>


    <!-- Add new control objective -->
    <b-modal ref="add-control-objective" @hide="hideCreateControlObjectiveModal" modal-class="task-modal" hide-footer>
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideCreateControlObjectiveModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="add-co-title">
            {{ $t('maintenance.control_objective.create.title') }}
          </h5>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="add-co-error"
      >
        {{ error }}
      </b-alert>

      <b-form @submit="coFormSubmit" qid="create-co-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-control-objective-name-label">
              {{ $t('maintenance.control_objective.create.name_label') }}<span class="mandatory"> *</span>
            </label>
            <b-form-input
                qid="create-control-objective-form-name"
                type="text"
                v-model="coForm.name"
                :maxLength="255"
                required
                :placeholder="$t('maintenance.control_objective.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="create-control-objective-description-label">
              {{ $t('maintenance.control_objective.create.description_label') }}
            </label>
            <b-form-textarea
                qid="create-control-objective-form-description"
                v-model="coForm.description"
                rows="6"
                max-rows="6"
                :placeholder="$t('maintenance.control_objective.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>
        </div>

        <div class="proxy-footer">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class=""
                            variant="secondary"
                            @click="hideCreateControlObjectiveModal"
                            qid="create-control-objective-cancel-button"
                  >
                    {{ $t('maintenance.control_objective.create.cancel') }}
                  </b-button>
                  <b-button type="submit"
                            class="ml-3"
                            variant="success"
                            :disabled="buttonDisabled"
                            qid="create-control-objective-submit-button"
                  >
                        <span v-if="buttonDisabled" class="mr-1">
                          <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                        </span>
                    {{ $t('maintenance.control_objective.create.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-form>
    </b-modal>


    <!-- Edit framework control modal template -->
    <b-modal ref="edit-framework-control" @hide="hideEditFrameworkControlModal" modal-class="task-modal" size="xl" hide-footer>
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideEditFrameworkControlModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="update-fc-title">
            {{ $t('maintenance.framework_control.update.title') }}
          </h5>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-fc-error"
      >
        {{ error }}
      </b-alert>

      <b-form @submit="editFrameworkControlFormSubmit" qid="edit-framework-control-form">
        <div class="form-container">
          <div class="row">
            <div class="col-sm-6 pr-4">
              <div class="text-right">
                <span class="btn btn-xs btn-success cursor-pointer"
                      @click="frameworkControlDisabled = false"
                      v-if="frameworkControlDisabled"
                >
                  {{ $t('system.edit') }}
                </span>
              </div>
              <b-form-group>
                <label qid="edit-framework-control-reference-label">
                  {{ $t('maintenance.framework_control.create.reference_label') }}<span v-if="!frameworkControlDisabled" class="mandatory"> *</span>
                </label>
                <b-form-input
                    qid="edit-framework-control-form-reference"
                    type="text"
                    v-model="fcForm.reference"
                    :maxLength="255"
                    required
                    v-if="!frameworkControlDisabled"
                    :placeholder="$t('maintenance.framework_control.create.reference_placeholder')"
                ></b-form-input>
                <p v-if="frameworkControlDisabled">{{ fcForm.reference }}</p>
              </b-form-group>
              <b-form-group>
                <label qid="edit-framework-control-name-label">
                  {{ $t('maintenance.framework_control.create.name_label') }}<span v-if="!frameworkControlDisabled" class="mandatory"> *</span>
                </label>
                <b-form-input
                    qid="edit-framework-control-form-name"
                    type="text"
                    v-model="fcForm.name"
                    :maxLength="255"
                    required
                    v-if="!frameworkControlDisabled"
                    :placeholder="$t('maintenance.framework_control.create.name_placeholder')"
                ></b-form-input>
                <p v-if="frameworkControlDisabled">{{ fcForm.name }}</p>
              </b-form-group>

              <b-form-group>
                <label qid="edit-framework-control-description-label">
                  {{ $t('maintenance.framework_control.create.description_label') }}<span v-if="!frameworkControlDisabled" class="mandatory"> *</span>
                </label>
                <b-form-textarea
                    qid="edit-framework-control-form-description"
                    v-model="fcForm.description"
                    required
                    rows="6"
                    max-rows="6"
                    v-if="!frameworkControlDisabled"
                    :placeholder="$t('maintenance.framework_control.create.description_placeholder')"
                ></b-form-textarea>
                <div v-if="frameworkControlDisabled">
                  <div v-if="fcForm.description && fcForm.description.length > 300">
                    <p v-if="showFullDescription" class="">
                      {{ fcForm.description }}
                    </p>
                    <p v-if="!showFullDescription" class="">
                      {{ fcForm.description | truncate(300) }}
                    </p>
                    <div class="text-center mt-3" @click="showFullDescription=!showFullDescription">
                      <span class="cursor-pointer" v-if="!showFullDescription">
                        <span class="mr-2">
                          {{ $t('system.view_more') }}
                        </span>
                        <font-awesome-icon icon="chevron-down"/>
                      </span>
                      <span class="cursor-pointer" v-if="showFullDescription">
                        <span class="mr-2">
                          {{ $t('system.view_less') }}
                        </span>
                        <font-awesome-icon icon="chevron-up"/>
                      </span>
                    </div>
                  </div>
                  <p v-if="fcForm.description && fcForm.description.length <= 300" class="">
                    {{ fcForm.description }}
                  </p>
                </div>
              </b-form-group>

              <b-form-group>
                <label qid="create-framework-control-objective-label">
                  {{ $t('maintenance.framework_control.create.control_objective_label') }}<span v-if="!frameworkControlDisabled" class="mandatory"> *</span>
                </label>
                <div class="multiselect-row" v-if="!frameworkControlDisabled">
                  <multiselect
                      v-model="fcForm.control_objective"
                      :options="controlObjectives"
                      :placeholder="$t('maintenance.framework_control.create.control_objective_placeholder')"
                      label="name"
                      :select-label="$t('system.dropdown_select_label')"
                      :selected-label="$t('system.dropdown_selected_label')"
                      :deselect-label="$t('system.dropdown_deselect_label')"
                      required
                      :allow-empty="false"
                      @search-change="loadCO"
                      track-by="id"
                      qid="create-framework-control-form-control-objective"
                  >
                    <template slot="singleLabel" slot-scope="props">
                      <font-awesome-icon icon="book"/>
                      <span class="ml-2">
                        {{ props.option.name }}
                      </span>
                    </template>
                    <template slot="option" slot-scope="props">
                      <font-awesome-icon icon="book"/>
                      <span class="ml-2">
                        {{ props.option.name }}
                      </span>
                    </template>
                  </multiselect>
                  <b-button
                      class="btn btn-secondary float-right"
                      @click="showCreateControlObjectiveModal()"
                      qid="control-objective-create-button-fc-modal"
                      v-if="isGranted(permissions.CONTROL_OBJECTIVE_CREATE) && (!getClient.parent || (getClient.parent && object.partition))"
                      v-b-tooltip.hover
                      :title="$t('maintenance.control_objective.add_new')"
                  >
                    <font-awesome-icon icon="plus-circle"/>
                  </b-button>
                </div>
                <p v-if="frameworkControlDisabled && fcForm.control_objective && fcForm.control_objective.name">
                  <font-awesome-icon icon="book"/>
                  {{ fcForm.control_objective.name }}
                </p>
              </b-form-group>
            </div>
            <div class="col-sm-6 pl-4">
              <b-form-group>
                <label qid="edit-ac-provisioning-label">
                  {{ $t('maintenance.framework_control.create.statement_of_applicability_label') }}
                </label>
                <div class="row">
                  <div class="col-sm-6">
                    <p class="text-normal mt-2">{{ $t('maintenance.framework_control.create.applicable_label') }}</p>
                  </div>
                  <div class="col-sm-6 text-right">
                    <b-form-radio-group
                      class="mt-2"
                      v-model="fcForm.statement_of_applicability"
                      :options="radioOptions"
                      qid="edit-ac-form-provisioning"
                    ></b-form-radio-group>
                  </div>
                </div>
              </b-form-group>
              <b-form-group>
                <label qid="create-framework-control-description-label">
                  {{ $t('maintenance.framework_control.create.soa_description_label') }}
                </label>
                <b-form-textarea
                    qid="create-framework-control-form-description"
                    v-model="fcForm.statement_of_applicability_description"
                    rows="6"
                    max-rows="6"
                    :placeholder="$t('maintenance.framework_control.create.soa_description_placeholder')"
                ></b-form-textarea>
              </b-form-group>
              <b-form-group>
                <label qid="edit-f-implementation-status-label">
                  {{ $t('maintenance.policies.create.implementation_status_label') }}
                </label>
                <multiselect
                    v-model="fcForm.implementation_status"
                    :options="statusItems"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    :placeholder="$t('maintenance.policies.create.implementation_status_placeholder')"
                    label="name"
                    qid="edit-f-form-implementation-status"
                >
                  <template slot="singleLabel" slot-scope="props">
                    <font-awesome-icon icon="exclamation-triangle" class="risk-16 mr-2" v-if="props.option.value == 'Not designed'"/>
                    <font-awesome-icon icon="exclamation-triangle" class="risk-9 mr-2" v-if="props.option.value == 'Designed but not implemented' || props.option.value == 'Partially implemented'"/>
                    <font-awesome-icon icon="exclamation-triangle" class="risk-1 mr-2" v-if="props.option.value == 'Fully implemented'"/>
                    <span class="ml-2">
                      {{ props.option.name }}
                    </span>
                  </template>
                  <template slot="option" slot-scope="props">
                    <font-awesome-icon icon="exclamation-triangle" class="risk-16 mr-2" v-if="props.option.value == 'Not designed'"/>
                    <font-awesome-icon icon="exclamation-triangle" class="risk-9 mr-2" v-if="props.option.value == 'Designed but not implemented' || props.option.value == 'Partially implemented'"/>
                    <font-awesome-icon icon="exclamation-triangle" class="risk-1 mr-2" v-if="props.option.value == 'Fully implemented'"/>
                    <span class="ml-2">
                      {{ props.option.name }}
                    </span>
                  </template>
                </multiselect>
              </b-form-group>
            </div>
            <div class="col-sm-12">
              <div class="text-center mt-4 mb-4">
                <span class="cursor-pointer"
                      @click="linkedControlsShow = !linkedControlsShow"
                      qid="data-discovery-validation-expand"
                >
                  <span class="mr-2">{{ $t('maintenance.framework_control.create.linked_controls_expand_label') }}</span>
                  <font-awesome-icon icon="chevron-down" v-if="!linkedControlsShow"/>
                  <font-awesome-icon icon="chevron-up" v-if="linkedControlsShow"/>
                </span>
              </div>

              <div v-if="linkedControlsShow">
                <hr class="mb-0">

                <b-tabs>
                  <!-- Generic controls tab -->
                  <b-tab :title="$t('generic_controls.title')" active>
                    <div class="row mt-4">
                      <div class="col-sm-12 text-right">
                        <span class="btn btn-success cursor-pointer"
                              qid="frameworkControl-create-button"
                              @click="showAddGenericControlModal"
                        >
                          <font-awesome-icon icon="plus"/> {{ $t('generic_controls.framework_controls.create.generic_control_title') }}
                        </span>
                      </div>
                      <div class="col-sm-12">
                        <table class="table table-responsive-md table-list-secondary">
                          <thead>
                            <tr>
                              <th scope="col">
                                {{ $t('generic_controls.table.name') }}
                              </th>
                              <th>
                                {{ $t('table.last_modified') }}
                              </th>
                              <th scope="col">
                                {{ $t('table.last_performed') }}
                              </th>
                              <th scope="col" width="100"></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(object, index) in genericControlItems" :key="'table-row-' + index" :id="'table-row-' + index">
                              <td>
                                <router-link
                                    :to="{ name: 'generic_controls.view', params: { slug: object.slug }}"
                                    qid="s-view-page-link"
                                >
                                  {{ object.name }}
                                </router-link>
                              </td>
                              <td>
                                <span v-if="object.updated_by">
                                  <Avatar :object="object.updated_by" size="xs" class="mr-2"></Avatar>
                                  {{ object.updated_at | formatDate }}
                                </span>
                              </td>
                              <td>
                                <span v-if="object.last_performed_all">
                                  <Avatar :object="object.last_performed_all.completed_by" size="xs" class="mr-2"></Avatar>
                                  {{ object.last_performed_all.completed_at | formatDate }}
                                </span>
                                <span v-else>
                                  {{ $t('table.never_performed') }}
                                </span>
                              </td>
                              <td align="right">
                                <span v-if="isGranted([permissions.FRAMEWORK_CONTROL_EDIT, permissions.FRAMEWORK_CONTROL_EDIT], object) && (!getClient.parent || (getClient.parent && object.partition))"
                                      class="btn btn-delete cursor-pointer"
                                      qid="delete-policy-modal"
                                      @click="showGenericControlConfirmDeleteModal(object)"
                                >
                                  <font-awesome-icon icon="trash-alt"/>
                                </span>
                              </td>
                            </tr>
                            <tr v-if="!loading && !genericControlItems.length" qid="no-results-row" class="text-center">
                              <td colspan="10">
                                <h5 class="mt-3">
                                  {{ $t('system.no_results') }}
                                </h5>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </b-tab>

                  <!-- Policies tab -->
                  <b-tab :title="$t('maintenance.policies.title')" v-if="getClient.policyModule">
                    <div class="row mt-4">
                      <div class="col-sm-12 text-right">
                        <span class="btn btn-success cursor-pointer"
                              qid="policy-create-button"
                              @click="showCreatePolicyModal"
                              v-if="isGranted([permissions.FRAMEWORK_CONTROL_EDIT, permissions.FRAMEWORK_CONTROL_EDIT_OWN], object) && (!getClient.parent || (getClient.parent && object.partition))"
                        >
                          <font-awesome-icon icon="plus"/> {{ $t('processes.policies.create_button') }}
                        </span>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-sm-12">
                        <table class="table table-responsive-md table-list-secondary">
                          <thead>
                            <tr>
                              <th scope="col">
                                {{ $t('processes.policies.table.name') }}
                              </th>
                              <th scope="col">
                                {{ $t('maintenance.policies.table.category') }}
                              </th>
                              <th scope="col">
                                {{ $t('system.last_updated') }}
                              </th>
                              <th scope="col" width="100"></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(item, index) in policyTableItems" :key="'policy-row-' + index" :id="'policy-row-' + index">
                              <td scope="row">
                                <router-link
                                    :to="{ name: 'policies.view', params: { slug: item.slug, client: item.client }}"
                                    qid="s-view-page-link"
                                >
                                  {{ item.name }}
                                </router-link>
                              </td>
                              <td>
                                <span qid="policy-category"
                                      :title="$t('maintenance.policies.category.' + $options.filters.slugify(item.category))"
                                      v-if="item.category"
                                      class="text-normal"
                                >
                                  <font-awesome-icon icon="shapes"/>
                                  {{ $t('maintenance.policies.category.' + $options.filters.slugify(item.category)) }}
                                </span>
                              </td>
                              <td>
                                <span class="text-normal">
                                  {{ item.updated_at | formatDate }}
                                </span>
                              </td>
                              <td align="right">
                                <span v-if="isGranted([permissions.FRAMEWORK_CONTROL_EDIT, permissions.FRAMEWORK_CONTROL_EDIT], object) && (!getClient.parent || (getClient.parent && object.partition))"
                                      class="btn btn-delete cursor-pointer"
                                      qid="delete-policy-modal"
                                      @click="showPolicyConfirmDeleteModal(item)"
                                >
                                  <font-awesome-icon icon="trash-alt"/>
                                </span>
                              </td>
                            </tr>
                            <tr v-if="!loading && !policyTableItems.length" qid="no-results-row" class="text-center">
                              <td colspan="10">
                                <h5 class="mt-3">
                                  {{ $t('system.no_results') }}
                                </h5>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </b-tab>

                  <!-- Questions tab -->
                  <b-tab :title="$t('maintenance.questions.label')">
                    <div class="row mt-4">
                      <div class="col-sm-12">
                        <table class="table table-responsive-md table-list-secondary">
                          <thead>
                            <tr>
                              <th scope="col">
                                {{ $t('generic_controls.table.name') }}
                              </th>
                              <th scope="col">
                                {{ $t('maintenance.assessments.title') }}
                              </th>
                              <th>
                                {{ $t('table.last_modified') }}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(object, index) in questionItems" :key="'table-row-' + index" :id="'table-row-' + index">
                              <td>
                                <span>
                                  {{ object.name }}
                                </span>
                              </td>
                              <td>
                                <span>
                                  <font-awesome-icon icon="file-alt"/>
                                  {{ object.assessment.name }}
                                </span>
                              </td>
                              <td>
                                <span v-if="object.updated_by">
                                  <Avatar :object="object.updated_by" size="xs" class="mr-2"></Avatar>
                                  {{ object.updated_at | formatDate }}
                                </span>
                              </td>
                            </tr>
                            <tr v-if="!loading && !questionItems.length" qid="no-results-row" class="text-center">
                              <td colspan="10">
                                <h5 class="mt-3">
                                  {{ $t('system.no_results') }}
                                </h5>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </b-tab>
                </b-tabs>

              </div>
            </div>
          </div>

        </div>
        <div class="proxy-footer-xl">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class=""
                            variant="secondary"
                            @click="hideEditFrameworkControlModal"
                            qid="edit-framework-control-cancel-button"
                  >
                    {{ $t('maintenance.framework_control.create.cancel') }}
                  </b-button>
                  <b-button type="submit"
                            class="ml-3"
                            variant="success"
                            :disabled="buttonDisabled"
                            qid="edit-framework-control-submit-button"
                  >
                        <span v-if="buttonDisabled" class="mr-1">
                          <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                        </span>
                    {{ $t('maintenance.framework_control.create.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-form>
    </b-modal>


    <!-- Delete policy modal template -->
    <b-modal ref="delete-policy" modal-class="task-modal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hidePolicyConfirmDeleteModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="delete-p-title">
            {{ $t('processes.policies.delete.title') }}
          </h5>
        </div>
      </template>
      <div class="mb-2 mt-3" style="font-size:18px;">
        <div>
          {{ $t('processes.policies.delete.description') }}
          <span class="text-bold">
            {{ itemToDelete.name }}
          </span>
        </div>
        <div class="mt-2">
          {{ $t('processes.policies.delete.are_you_sure') }}
        </div>
      </div>

      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class="mt-3"
                            variant="secondary"
                            @click="hidePolicyConfirmDeleteModal"
                            qid="delete-policy-cancel-button"
                  >
                    {{ $t('maintenance.measures.delete.cancel') }}
                  </b-button>
                  <b-button @click="confirmDeletePolicy(itemToDelete)"
                            class="mt-3 ml-3"
                            variant="danger"
                            qid="delete-policy-submit-button"
                            :disabled="buttonDisabled"
                            v-if="isGranted([permissions.FRAMEWORK_CONTROL_EDIT, permissions.FRAMEWORK_CONTROL_EDIT_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
                  >
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                    </span>
                    {{ $t('maintenance.measures.delete.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-modal>


    <!-- Delete policy modal template -->
    <b-modal ref="delete-generic-control" modal-class="task-modal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideGenericControlConfirmDeleteModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="delete-p-title">
            {{ $t('processes.policies.delete.title') }}
          </h5>
        </div>
      </template>
      <div class="mb-2 mt-3" style="font-size:18px;">
        <div>
          {{ $t('processes.policies.delete.description') }}
          <span class="text-bold">
            {{ itemToDelete.name }}
          </span>
        </div>
        <div class="mt-2">
          {{ $t('processes.policies.delete.are_you_sure') }}
        </div>
      </div>

      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class="mt-3"
                            variant="secondary"
                            @click="hideGenericControlConfirmDeleteModal"
                            qid="delete-policy-cancel-button"
                  >
                    {{ $t('maintenance.measures.delete.cancel') }}
                  </b-button>
                  <b-button @click="confirmDeleteGenericControl(itemToDelete)"
                            class="mt-3 ml-3"
                            variant="danger"
                            qid="delete-policy-submit-button"
                            :disabled="buttonDisabled"
                            v-if="isGranted([permissions.FRAMEWORK_CONTROL_EDIT, permissions.FRAMEWORK_CONTROL_EDIT_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
                  >
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                    </span>
                    {{ $t('maintenance.measures.delete.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-modal>


    <!-- Add new policy -->
    <b-modal ref="add-policy" @hide="hideCreatePolicyModal" modal-class="task-modal" hide-footer>
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideCreatePolicyModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="add-p-title">
            {{ $t('processes.policies.create.title') }}
          </h5>
        </div>
      </template>

      <b-alert v-if="policyError.length"
               show
               variant="danger"
               qid="add-p-error"
      >
        {{ policyError }}
      </b-alert>

      <b-form @submit="policyFormSubmit" qid="add-policy-form">
        <div class="form-container">
          <b-form-group>
            <label qid="add-policy-pg-label">
              {{ $t('processes.policies.create.policy_label') }}
            </label>
            <multiselect
                v-model="selectedPolicy"
                :options="policyItems"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('processes.policies.create.policy_placeholder')"
                label="name"
                @search-change="loadPolicy"
                track-by="slug"
                qid="add-p-form-pg"
            ></multiselect>
          </b-form-group>
          <div v-if="selectedPolicy && selectedPolicy.description"
               class="text-center mb-3 mt-4"
          >
            <span class="cursor-pointer">
              <span class="mr-2"
                    @click="showPolicyDescription = !showPolicyDescription"
                    qid="expand-policy-description"
              >
                {{$t('processes.policies.create.policy_description_expand')}}
              </span>
              <font-awesome-icon icon="chevron-down" v-if="!showPolicyDescription"/>
              <font-awesome-icon icon="chevron-up" v-if="showPolicyDescription"/>
            </span>
          </div>
          <div v-if="selectedPolicy && selectedPolicy.description && showPolicyDescription" class="mb-4 mt-2">
            <span class="text-formatted">
              {{ selectedPolicy.description }}
            </span>
          </div>
        </div>
        <div class="proxy-footer">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class=""
                            variant="secondary"
                            @click="hideCreatePolicyModal"
                            qid="add-p-cancel-button"
                  >
                    {{ $t('processes.policies.create.cancel') }}
                  </b-button>
                  <b-button type="submit"
                            class="ml-3"
                            variant="success"
                            :disabled="buttonDisabled"
                            qid="add-p-submit-button"
                  >
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                    </span>
                    {{ $t('processes.policies.create.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-form>
    </b-modal>


    <!-- View Framework Control modal template -->
    <b-modal ref="view-framework-control" @hide="hideViewFCModal" modal-class="task-modal" hide-footer>
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideViewFCModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="view-fc-title">
            {{ $t('maintenance.framework_control.title') }}
          </h5>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="view-fc-error"
      >
        {{ error }}
      </b-alert>

      <div v-if="selectedFrameworkControl">
        <div v-if="selectedFrameworkControl.reference">
          <label>{{ $t('maintenance.framework_control.create.reference_label') }}</label>
            <p class="text-formatted">{{ selectedFrameworkControl.reference }}</p>
          <br>
        </div>
        <div v-if="selectedFrameworkControl.name">
          <label>{{ $t('maintenance.framework_control.create.name_label') }}</label>
            <p class="text-formatted">{{ selectedFrameworkControl.name }}</p>
          <br>
        </div>
        <div v-if="selectedFrameworkControl.description">
          <label>{{ $t('maintenance.framework_control.create.description_label') }}</label>
            <p class="text-formatted">{{ selectedFrameworkControl.description }}</p>
          <br>
        </div>
        <div v-if="selectedFrameworkControl.description">
          <label qid="view-framework-control-soa-label">
            {{ $t('maintenance.framework_control.create.statement_of_applicability_label') }}
          </label>
        </div>

        <p v-if="selectedFrameworkControl.statement_of_applicability">
          <font-awesome-icon icon="check-circle" class="icon-success"/>
          {{ $t('system.yes') }}
        </p>
        <p v-if="!selectedFrameworkControl.statement_of_applicability">
          <font-awesome-icon icon="times-circle" class="icon-danger"/>
          {{ $t('system.no') }}
        </p>
        <br>

        <div v-if="selectedFrameworkControl.statement_of_applicability_description">
          <label qid="create-framework-control-description-label">
            {{ $t('maintenance.framework_control.create.soa_description_label') }}
          </label>
          <p class="text-formatted">{{ selectedFrameworkControl.statement_of_applicability_description }}</p>
          <br>
        </div>
        <div v-if="selectedFrameworkControl.implementation_status">
          <label qid="edit-p-implementation-status-label">
            {{ $t('maintenance.policies.create.implementation_status_label') }}
          </label>
          <div class="" qid="view-fc-implementation_status">
            <font-awesome-icon icon="exclamation-triangle" class="risk-1 mr-2" v-if="selectedFrameworkControl.implementation_status == 'Fully implemented'"/>
            <font-awesome-icon icon="exclamation-triangle" class="risk-9 mr-2" v-if="selectedFrameworkControl.implementation_status == 'Designed but not implemented' || selectedFrameworkControl.implementation_status == 'Partially implemented'"/>
            <font-awesome-icon icon="exclamation-triangle" class="risk-16 mr-2" v-if="selectedFrameworkControl.implementation_status == 'Not designed'"/>
            {{ $t('maintenance.policies.status.' + $options.filters.slugify(selectedFrameworkControl.implementation_status)) }}
          </div>
          <br>
        </div>
      </div>
      <div class="proxy-footer">
        <div class="alert alert-menu-color color-secondary mb-0" role="alert">
          <div class="row">
            <div class="col-sm-6">

            </div>
            <div class="col-sm-6">
              <div class="text-right">
                <b-button class="float-right"
                          variant="secondary"
                          @click="hideViewFCModal"
                          qid="view-fc-cancel-button"
                >
                  {{ $t('processes.policies.view.close') }}
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>


    <!-- Add new generic control -->
    <b-modal ref="add-generic-control" hide-footer @hide="hideAddGenericControlModal" modal-class="task-modal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideAddGenericControlModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="update-a-title">
            {{ $t('generic_controls.framework_controls.create.generic_control_title') }}
          </h5>
        </div>
      </template>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-io-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="genericControlFormSubmit" qid="create-framework-control-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-framewor-control-pg-label">
              {{ $t('generic_controls.framework_controls.create.generic_control_label') }}
            </label>
            <multiselect
                v-model="selectedGenericControl"
                :options="genericControlDropdownItems"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('generic_controls.framework_controls.create.generic_control_placeholder')"
                label="name"
                @search-change="loadGenericControlItems"
                track-by="id"
                qid="create-lb-form-pg"
            ></multiselect>
          </b-form-group>
        </div>

        <div class="proxy-footer">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class="mt-1"
                            variant="secondary"
                            @click="hideAddGenericControlModal"
                            qid="create-p-cancel-button"
                  >
                    {{ $t('generic_controls.framework_controls.create.cancel') }}
                  </b-button>
                  <b-button type="submit"
                            class="mt-1 ml-3"
                            variant="success"
                            :disabled="buttonDisabled"
                            qid="create-p-submit-button"
                  >
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                    </span>
                    {{ $t('generic_controls.framework_controls.create.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-form>
    </b-modal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Avatar from '@/components/Avatar.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';
  import FrameworkSideFilter from '@/components/frameworks/FrameworkSideFilter.vue';
  import FrameworkTopBar from '@/components/frameworks/FrameworkTopBar.vue';


  export default {
    name: 'FrameworkControlsTable',
    data: function() {
      return {
        fcForm: {
          name: '',
          reference: '',
          description: '',
          control_objective: [],
          statement_of_applicability_description: '',
          statement_of_applicability: '',
          implementation_status: []
        },
        coForm: {
          name: '',
          description: '',
        },
        error: '',
        policyError: '',
        loadingItems: false,
        selectedFrameworkControl: [],
        selectedFrameworkControlSort: '',
        tableItems: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,


        selectedFilter: {},
        selectedFilterName: '',

        selectedControlObjective: [],
        selectedCO: [],
        selectedControlObjectiveDeleted: false,

        itemToDelete: {},
        buttonDisabled: false,
        popoverSpinner: false,
        popoverSpinnerCounter: 0,
        controlObjectives: [],
        loading: true,
        selectedItem: {},
        filters: {
          control_objective: {},
          keywordValue: ''
        },
        SideFilterRenderComponent: true,
        updateChildList: false,
        radioOptions: [
          {text: this.$t('system.yes'), value: 1},
          {text: this.$t('system.no'), value: 0}
        ],
        statusItems: [
          { name: this.$t('maintenance.policies.status.not_designed'), value: 'Not designed' },
          { name: this.$t('maintenance.policies.status.designed_but_not_implemented'), value: 'Designed but not implemented' },
          { name: this.$t('maintenance.policies.status.partially_implemented'), value: 'Partially implemented' },
          { name: this.$t('maintenance.policies.status.fully_implemented'), value: 'Fully implemented' }
        ],
        linkedControlsShow: false,
        genericControlItems: [],
        questionItems: [],
        policyTableItems: [],
        policyItems: [],
        selectedPolicy: [],
        showPolicyDescription: false,
        frameworkControlDisabled: true,
        showFullDescription: false,
        myGuides: [],
        genericControlDropdownItems: [],
        selectedGenericControl: [],
        selectedFrameworkControl: []
      }
    },
    props: {
      framework: {
        type: Object,
        required: true
      },
      tasks: {
        type: Array
      }
    },
    components: {
      Avatar,
      FrameworkSideFilter,
      FrameworkTopBar,
      GuideButton
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getClient() {
        return this.$store.state.client
      },
      getFilters() {
        return this.$store.state.activeFilters
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getWordpressLink() {
        return this.$store.state.wordpressLink
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadContent();
        this.loadCO();
      },
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent()
      }, 400),
      getTask(code) {
        let existingTask = _.find(this.tasks, function(object) {
          return object.code == code
        });
        return existingTask
      },
      openToolbar(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Maintenance'
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Maintenance',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      showAddGenericControlModal() {
        this.selectedGenericControl = [];
        this.loadGenericControlItems();
        this.$refs['add-generic-control'].show()
      },
      hideAddGenericControlModal() {
        this.error = ''
        this.selectedGenericControl = [];
        this.$refs['add-generic-control'].hide()
      },
      genericControlFormSubmit(evt) {
        this.buttonDisabled = true;
        let formData = new FormData();
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        if (this.selectedGenericControl && this.selectedGenericControl.id) {
          formData.append("genericControl", this.selectedGenericControl.slug);
        }

        if (this.selectedFrameworkControl && this.selectedFrameworkControl.id) {
          formData.append("framework_control_id", this.selectedFrameworkControl.id);
        }

        piincHttp.post('/generic-controls/' + this.selectedGenericControl.slug + '/framework-controls', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function(response) {
          self.$toastr('success', self.$t('maintenance.framework_control.generic_control_add_success'))
          self.hideAddGenericControlModal();
          self.buttonDisabled = false;
          self.loadGenericControls(self.selectedFrameworkControl.id);
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
            if (error.data.errors.framework_control_id) {
              self.error += ' ' + self.$t('maintenance.framework_control.generic_control_error')
            }
          }
        })
      },
      loadGenericControlItems(query) {
        let self = this;
        piincHttp.get('generic-controls', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.genericControlDropdownItems = response.data.items
          if (self.genericControlDropdownItems && !self.genericControlDropdownItems.length && !query) {
            self.genericControlDropdownItems.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      handleSearch() {
        this.selectedControlObjective = [];
        this.filters.control_objective = []
        this.debounceInput()
      },
      updateSelectedFilter(item) {
        if (!item) {
          this.filters.control_objective = []
          this.selectedFilterName = ''
          this.selectedControlObjective = []
        }else{
          this.filters.control_objective = item
          this.selectedFilterName = item.name
          this.selectedControlObjective = this.filters.control_objective;
        }

        this.debounceInput()
      },
      forceSideFilterRenderComponent(object) {
        // store selected control_objective provided from the top bar component
        if (object) {
          this.selectedControlObjective = object
        }else{
          this.selectedControlObjectiveDeleted = true
          this.updateSelectedFilter()
        }
      },
      showFCPopover(object) {
        let self = this;
        self.popoverSpinner = true;
        self.popoverSpinnerCounter = 0;

        // self.loadPolicies(object.id)  // TypeError: self.loadPolicies is not a function
        piincHttp.get('policies', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            framework_control: object.id,
          }
        }).then(function(response) {
          self.policyTableItems = response.data.items

          self.popoverSpinnerCounter++
          if (self.popoverSpinnerCounter == 2) {
            self.popoverSpinner = false;
          }
        }, function() {});

        piincHttp.get('generic-controls', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            framework_control: object.id,
            last_performed_all: 1
          }
        }).then(function(response) {
          self.genericControlItems = response.data.items

          self.popoverSpinnerCounter++
          if (self.popoverSpinnerCounter == 2) {
            self.popoverSpinner = false;
          }
        }, function() {});
      },
      hideFCPopover() {
        let self = this;

        this.popoverSpinner = true
        this.popoverSpinnerCounter = 0

        this.policyTableItems = []
        this.genericControlItems = []
      },
      openViewFCModal(object) {
        this.error = ''
        this.selectedFrameworkControl = object;
        this.$refs['view-framework-control'].show()
      },
      hideViewFCModal () {
        this.$refs['view-framework-control'].hide()
      },
      showEditControlObjectiveModal(object) {
        this.coForm.slug = object.slug;
        this.coForm.name = object.name;
        this.coForm.description = object.description;
        this.$refs['edit-control-objective'].show()
      },
      hideEditControlObjectiveModal() {
        this.frameworkControlDisabled = true
        this.$refs['edit-control-objective'].hide()
      },
      showEditFrameworkControlModal(object) {
        let self = this;
        this.error = ''

        this.selectedFrameworkControl = object;
        this.frameworkControlDisabled = true;

        this.loadCO();
        this.loadGenericControls(object.id);
        this.loadQuestions(object.id);
        this.loadPolicies(object.id);
        this.loadPolicy();

        this.fcForm.control_objective = []
        // Preselect control_objective on edit modal
        if (object.control_objective) {
          _.forEach(this.controlObjectives, function(item) {
            if (item.id === object.control_objective) {
              self.fcForm.control_objective = item;
            }
          });
        }

        this.fcForm.implementation_status = []
        if (object.implementation_status) {
          this.fcForm.implementation_status = _.find(this.statusItems, function(item) {
            return item.value === object.implementation_status;
          });
        }

        this.fcForm.slug = object.slug;
        this.fcForm.name = object.name;
        this.fcForm.reference = object.reference;
        this.fcForm.description = object.description;
        this.fcForm.statement_of_applicability_description = object.statement_of_applicability_description;
        this.fcForm.statement_of_applicability = object.statement_of_applicability;

        this.$refs['edit-framework-control'].show()
      },
      hideEditFrameworkControlModal() {
        this.selectedFrameworkControl = {};
        this.$refs['edit-framework-control'].hide()
      },
      editFrameworkControlFormSubmit(evt) {
        let self = this;
        evt.preventDefault();
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned

        this.fcForm.client = this.getClient.slug;

        this.fcForm.control_objective = this.fcForm.control_objective ? this.fcForm.control_objective.id : ''

        this.fcForm.implementation_status = this.fcForm.implementation_status ? this.fcForm.implementation_status.value : '';

        if (this.fcForm.control_objective) {
          piincHttp.put('framework-controls/' + this.fcForm.slug, this.fcForm).then(function(response) {
            self.hideEditFrameworkControlModal();
            self.loadContent(self.currentPage);
            self.buttonDisabled = false;

            self.$toastr('success', self.$t('maintenance.framework_control.update.success'))
          }, function(error) {
            if (error.status !== 200) {
              this.buttonDisabled = false;
              this.error = error.data.message;
            }
          })
        } else {
          this.error = this.$t('maintenance.frameworks.create.control_objective_required')
          this.buttonDisabled = false;
        }
      },
      setValidation(object) {
        this.validation = object
      },
      showCreateFrameworkControlModal() {
        this.loadCO();
        this.error = ''
        this.fcForm.name = '';
        this.fcForm.reference = '';
        this.fcForm.description = '';
        this.fcForm.control_objective = null;
        this.fcForm.implementation_status = [];
        this.fcForm.statement_of_applicability = 1;
        this.fcForm.statement_of_applicability_description = '';
        this.$refs['add-framework-control'].show()
      },
      hideCreateFrameworkControlModal() {
        this.error = ''
        this.selectedFrameworkControl = []
        this.$refs['add-framework-control'].hide()
      },
      showCreateControlObjectiveModal() {
        this.frameworkControlDisabled = true;
        this.coForm.name = '';
        this.coForm.description = '';
        this.error = ''
        this.$refs['add-control-objective'].show()
      },
      hideCreateControlObjectiveModal() {
        this.coForm.name = '';
        this.coForm.description = '';
        this.error = ''
        this.$refs['add-control-objective'].hide()
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-framework-control'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-framework-control'].hide()
      },
      confirmDeleteFrameworkControl(object) {
        let self = this;
        this.buttonDisabled = true;

        piincHttp.delete('framework-controls/' + object.slug).then(function() {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          self.buttonDisabled = false;
          self.$toastr('success', self.$t('maintenance.framework_control.delete.success'))
        }, function() {});
      },
      fcFormSubmit(evt) {
        this.buttonDisabled = true;
        let formData = new FormData();

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();
        this.updateChildList = false;

        formData.append("name", this.fcForm.name);
        formData.append("reference", this.fcForm.reference);
        formData.append("description", this.fcForm.description);
        formData.append("statement_of_applicability_description", this.fcForm.statement_of_applicability_description);
        formData.append("statement_of_applicability", this.fcForm.statement_of_applicability);

        // Additional check - if implementation status is selected, append it to form data
        if (this.fcForm.implementation_status && this.fcForm.implementation_status.value) {
          formData.append("implementation_status", this.fcForm.implementation_status.value);
        }

        formData.append("client", this.getClient.slug);
        formData.append("framework", this.framework.id);

        if (this.selectedControlObjective && this.selectedControlObjective.id) {
          formData.append("control_objective", this.selectedControlObjective.id);
        }else{
          if (this.fcForm.control_objective && this.fcForm.control_objective.id) {
            formData.append("control_objective", this.fcForm.control_objective.id);
          }
        }

        if ((this.fcForm.control_objective && this.fcForm.control_objective.id) || (this.selectedControlObjective && this.selectedControlObjective.id)) {
          piincHttp.post('/framework-controls', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }).then(function(response) {
            self.hideCreateFrameworkControlModal();
            self.buttonDisabled = false;
            self.loadContent(self.currentPage);
            self.$toastr('success', self.$t('maintenance.framework_control.create.success'))
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
              if (error.data.errors.framework_control_id) {
                self.error += ' ' + error.data.errors.framework_control_id[0]
              }
            }
          })
        } else {
          this.error = this.$t('maintenance.frameworks.create.control_objective_required')
          this.buttonDisabled = false;
        }
      },
      coFormSubmit(evt) {
        let self = this;
        evt.preventDefault();

        this.buttonDisabled = true;

        this.coForm.client = this.getClient.slug;
        this.coForm.framework = this.framework.id;

        piincHttp.post('/control-objectives', this.coForm).then(function(response) {
          self.hideCreateControlObjectiveModal();
          self.buttonDisabled = false;
          self.loadCO(response.data);
          if (!self.frameworkControlDisabled) {
            self.fcForm.control_objective = response.data;
            self.selectedControlObjective = response.data;
          }

          self.$toastr('success', self.$t('maintenance.control_objective.create.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
            if (error.data.errors.framework_control_id) {
              self.error += ' ' + error.data.errors.framework_control_id[0]
            }
          }
        })
      },
      loadCO(selectedObject) {
        let self = this;
        piincHttp.get('/control-objectives', { params:
          {
            client: this.getClient.slug,
            framework: this.framework.id,
            per_page: 100,
            // keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.controlObjectives = response.data.items

          // If selected category is provided to this function, preselect it in dropdown
          if (selectedObject && selectedObject.id) {
            self.selectedControlObjective = selectedObject
          }

          if (self.controlObjectives && !self.controlObjectives.length && !query) {
            self.controlObjectives.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      loadGenericControls(framework_control_id = null) {
        let self = this;

        piincHttp.get('generic-controls', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            framework_control: framework_control_id,
            last_performed_all: 1
          }
        }).then(function(response) {
          self.genericControlItems = response.data.items
        }, function() {});
      },
      loadQuestions(framework_control_id = null) {
        let self = this;

        piincHttp.get('questions', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            framework_control: framework_control_id,
            assessment: 'all',
          }
        }).then(function(response) {
          self.questionItems = response.data.items
        }, function() {});
      },
      loadPolicies(framework_control_id = null) {
        let self = this;

        piincHttp.get('policies', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            framework_control: framework_control_id,
          }
        }).then(function(response) {
          self.policyTableItems = response.data.items
        }, function() {});
      },
      loadPolicy(query) {
        let self = this;
        piincHttp.get('policies', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.policyItems = response.data.items
          if (self.policyItems && !self.policyItems.length && !query) {
            self.policyItems.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      showCreatePolicyModal() {
        this.$refs['add-policy'].show()
      },
      hideCreatePolicyModal() {
        this.policyError = ''
        this.selectedPolicy = [];
        this.showPolicyDescription = false;
        this.$refs['add-policy'].hide()
      },
      showPolicyConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-policy'].show()
      },
      hidePolicyConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-policy'].hide()
      },
      confirmDeletePolicy(object) {
        let self = this;
        self.buttonDisabled = true;

        piincHttp.delete('framework-controls/' + this.selectedFrameworkControl.slug + '/policies/' + object.slug).then(function() {
          self.hidePolicyConfirmDeleteModal();
          self.loadPolicies(self.selectedFrameworkControl.id);
          self.buttonDisabled = false;
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('processes.policies.delete.success'))
        }, function() {});
      },
      showGenericControlConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-generic-control'].show()
      },
      hideGenericControlConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-generic-control'].hide()
      },
      confirmDeleteGenericControl(object) {
        let self = this;
        self.buttonDisabled = true;

        piincHttp.delete('/framework-controls/' + this.selectedFrameworkControl.slug + '/generic-controls/' + this.itemToDelete.slug).then(function() {
          self.hideGenericControlConfirmDeleteModal();
          self.loadGenericControls(self.selectedFrameworkControl.id);
          self.buttonDisabled = false;
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('maintenance.framework_control.generic_control_remove_success'))
        }, function() {});
      },
      policyFormSubmit(evt) {
        this.buttonDisabled = true;
        let formData = new FormData();
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        if (this.selectedPolicy && this.selectedPolicy.id) {
          formData.append("policy_id", this.selectedPolicy.id);
        }

        formData.append("framework_control_id", this.selectedFrameworkControl.id);

        if (this.selectedPolicy && this.selectedPolicy.id) {
          piincHttp.post('/policies/' + this.selectedPolicy.slug + '/framework-controls/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }).then(function(response) {
            self.$toastr('success', self.$t('processes.policies.create.success'))
            self.hideCreatePolicyModal();
            self.buttonDisabled = false;
            self.loadPolicies(self.selectedFrameworkControl.id);
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.policyError = error.data.message;
              if (error.data.errors.framework_control_id) {
                self.policyError += ' ' + error.data.errors.framework_control_id[0]
              }
            }
          })
        } else {
          this.policyError = ''
          this.buttonDisabled = false;
          if (!this.selectedPolicy || (this.selectedPolicy && !this.selectedPolicy.id)) {
            this.policyError +=  ' ' + this.$t('processes.policies.create.error')
          }
        }
      },
      loadContent(page, perPage) {
        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;

        piincHttp.get('framework-controls', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            sort: 'name',
            order: 'asc',
            keyword: this.filters.keywordValue !== '' ? this.filters.keywordValue : undefined,
            framework: this.framework.id,
            control_objective: this.filters.control_objective ? this.filters.control_objective.id : undefined,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.loading = false;
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      pageChanged(page) {
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load content when page changed listener is triggered
        this.loadContent(1, perPage.value)
        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
      },
      perPageRemoved() {
        let self = this;
        // Load content when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)
      }
    },
    beforeRouteLeave(to, from, next) {
      this.$store.commit('storePerPage', this.selectedPerPage);
      next()
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        this.loadContent();
        this.loadCO();
      }

      this.loadGuides('update_framework_control');
    }
  }
</script>
